/*!
 * Copyright Optimus Ride 2019
 */

import firebase from 'firebase';
import { WaypointDescription, toWaypointDescription } from './waypoint-list';
import { ControlCommandHeader as GrpcCommandHeader } from '../../grpc/common_pb';
import { RouteProposal as GrpcRouteProposal } from '../../grpc/route_proposal_pb';
import { RouteProgress as GrpcRouteProgress } from '../../grpc/route_progress_pb';
import {
    GetRouteProposalResponse,
} from '../../grpc/remote-monitor-command_pb';
import { snapshotToProtobuf } from '../../utils/firestore';

export interface RouteProgress {
    remainingWaypoints: Array<WaypointDescription>;
    status: RouteProgressStatus;
};

export enum RouteProposalResult {
    SUCCESS,
    NOT_IN_LANE,
    NO_ROUTE_TO_DEST,
    UNKNOWN_WAYPOINT_FOUND,
    CONFIRMATION_TIMEOUT,
    VEHICLE_PLANNING_LIMITATION,
    UNKNOWN,
}

export enum RouteProgressStatus {
    UNKNOWN,
    INACTIVE,
    RUNNING,
    SUCCESS,
    FAILURE,
}

export interface CommandHeader {
    utime: number;
    commandId: number;
    commanderId: string;
}

const EmptyCommandHeader = {
    utime: -1,
    commandId: -1,
    commanderId: ''
};

export interface RouteProposal {
    result: RouteProposalResult;
    commandId: CommandHeader;
    waypoints: WaypointDescription[];
}

export function toRouteProposal(response: GetRouteProposalResponse): RouteProposal {
    return {
        result: RouteProposalResult.SUCCESS, // TODO(peter-or) Parse result
        commandId: toCommandHeader(response.getHeader()),
        waypoints: response.getWaypointsList().map(toWaypointDescription)
    };
}

export function toRouteProposalResult(result: GrpcRouteProposal.RoutePlanResult): RouteProposalResult {
    switch(result) {
        case GrpcRouteProposal.RoutePlanResult.SUCCESS:
            return RouteProposalResult.SUCCESS;
        default:
            return RouteProposalResult.UNKNOWN;
    }
}

export const EmptyRouteProposal: RouteProposal = {
    result: RouteProposalResult.UNKNOWN,
    commandId: EmptyCommandHeader,
    waypoints: [],
};

export const EmptyRouteProgress: RouteProgress = {
    remainingWaypoints: [],
    status: RouteProgressStatus.UNKNOWN,
};

export function toRouteProgress(snapshot: firebase.firestore.DocumentSnapshot): RouteProgress {
    return protobufToRouteProgress(
      snapshotToProtobuf(snapshot, GrpcRouteProgress.deserializeBinary));
}

function protobufToRouteProgress(grpcRouteProgress: GrpcRouteProgress): RouteProgress {
    const grpcWaypointList = grpcRouteProgress.getWaypoints();
    const grpcWaypointDescriptions = grpcWaypointList ?
        grpcWaypointList.getWaypointsList() :
        [];

    return {
        remainingWaypoints: grpcWaypointDescriptions.map(toWaypointDescription),
        status: toRouteStatus(grpcRouteProgress.getStatus()),
    };
}

function toRouteStatus(status: GrpcRouteProgress.RouteProgressStatus): RouteProgressStatus {
    switch(status) {
        case GrpcRouteProgress.RouteProgressStatus.FAILURE:
            return RouteProgressStatus.FAILURE;
        case GrpcRouteProgress.RouteProgressStatus.INACTIVE:
            return RouteProgressStatus.INACTIVE;
        case GrpcRouteProgress.RouteProgressStatus.RUNNING:
            return RouteProgressStatus.RUNNING;
        case GrpcRouteProgress.RouteProgressStatus.SUCCESS:
            return RouteProgressStatus.SUCCESS;
        case GrpcRouteProgress.RouteProgressStatus.UNKNOWN:
            return RouteProgressStatus.UNKNOWN;
        default:
            throw new Error('Unknown Route Status enum value');
    }
}

export function fromCommandHeader(commandHeader: CommandHeader): GrpcCommandHeader {
    const grpcCommandHeader = new GrpcCommandHeader();
    grpcCommandHeader.setUtime(commandHeader.utime);
    grpcCommandHeader.setCommandid(commandHeader.commandId);
    grpcCommandHeader.setCommanderid(commandHeader.commanderId);

    return grpcCommandHeader;
}
export function toCommandHeader(controlCommandHeader: GrpcCommandHeader | undefined): CommandHeader {
    return controlCommandHeader === undefined ? EmptyCommandHeader : {
        utime: controlCommandHeader.getUtime(),
        commandId: controlCommandHeader.getCommandid(),
        commanderId: controlCommandHeader.getCommanderid()
    };
}
